import { useEffect, useMemo, useRef } from 'react';

import { type AtomsObservability, useAnalyticsContext } from '../utils/atoms-provider';

const noop: AtomsObservability['trackJourneyEvent'] = () => {};

/**
 * The fail-safe wrapper every instrumented component calls — never the context
 * directly. Swallows transport errors so an app that never initialized
 * observability doesn't crash.
 *
 * Defensive on two axes:
 * - **Resolution:** a host that returns a malformed value (null / missing
 *   method) falls back to no-op via `?.`. We deliberately do NOT wrap the hook
 *   call itself in try/catch — `useObservability` may call hooks, and swallowing
 *   a throw mid-hook-sequence would corrupt React's hook state (worse than a
 *   clean error). A host hook that throws synchronously is its own
 *   rules-of-hooks violation.
 * - **Dispatch:** the inner call is wrapped so a throwing transport is swallowed.
 *
 * Identity is stable by design (the returned wrapper is memoized with `[]`), so
 * components can list it in their own `useCallback` deps without busting
 * memoization every render — host `useObservability` hooks commonly return a
 * fresh function each render. To avoid the stale-closure tradeoff that a plain
 * captured value would have, the wrapper dispatches through a ref that tracks
 * the latest transport: if the host swaps it at runtime (e.g. consent
 * revocation), already-mounted components immediately use the new one.
 */
export function useObservabilitySafe(): AtomsObservability {
  const { useObservability } = useAnalyticsContext();
  const hostTrack = useObservability()?.trackJourneyEvent ?? noop;

  // Latest-ref pattern: keep identity stable AND always dispatch through the
  // current transport. Initialized with the first value so dispatches before
  // the first commit still work.
  const latestTrack = useRef<AtomsObservability['trackJourneyEvent']>(hostTrack);
  useEffect(() => {
    latestTrack.current = hostTrack;
  }, [hostTrack]);

  const trackJourneyEvent = useMemo<AtomsObservability['trackJourneyEvent']>(
    () => (categoryName, eventName, eventData, contextualData) => {
      try {
        latestTrack.current(categoryName, eventName, eventData, contextualData);
      } catch {
        // Telemetry must never crash the host app.
      }
    },
    [],
  );

  return useMemo(
    () => ({
      trackJourneyEvent,
    }),
    [trackJourneyEvent],
  );
}
